import React, { useCallback, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  isSubscribeModalOpenSelector,
  isUnsubscribeModalOpenSelector,
  isNotificationsSubscriptionPendingSelector,
} from 'store/modules/crm/selectors';
import FluidButton from 'ui/FluidButton';
import { theme } from '../../../../tailwind.config';
import * as CrmManagementActions from 'store/modules/crm/actions';
import RingingBellIcon from 'ui/Icons/ringing-bell.component.svg';
import StrikeBellIcon from 'ui/Icons/strike-bell.component.svg';
import { HidingTextTooltip } from 'ui/Tooltip/HidingTextTooltip';
import ConfirmationModal, { EConfirmationModalType } from 'ui/ConfirmationModal';

export interface ICrmDirectorySubscriptionButtonsProps {
  directoryEntriesCount: number;
  onSubscribeToAllNotifications: () => void;
  onUnsubscribeFromAllNotifications: () => void;
}

export const CrmDirectorySubscriptionButtons: React.FC<ICrmDirectorySubscriptionButtonsProps> = React.memo(
  ({ directoryEntriesCount, onSubscribeToAllNotifications, onUnsubscribeFromAllNotifications }) => {
    const dispatch = useDispatch();
    const isSubscribeModalOpen = useSelector(isSubscribeModalOpenSelector);
    const isUnsubscribeModalOpen = useSelector(isUnsubscribeModalOpenSelector);
    const isNotificationsSubscriptionPending = useSelector(isNotificationsSubscriptionPendingSelector);

    const openSubscribeToAllNotificationsModal = useCallback(() => {
      dispatch(CrmManagementActions.openSubscribeToAllNotificationsModalAction());
    }, []);

    const handleSubscribeToAllNotificationsConfirm = useCallback(() => {
      onSubscribeToAllNotifications();
    }, [onSubscribeToAllNotifications]);

    const closeSubscribeToAllNotificationsModal = useCallback(() => {
      dispatch(CrmManagementActions.closeSubscribeToAllNotificationsModalAction());
    }, []);

    const openUnsubscribeFromAllNotificationsModal = useCallback(() => {
      dispatch(CrmManagementActions.openUnsubscribeFromAllNotificationsModalAction());
    }, []);

    const handleUnsubscribeFromAllNotificationsConfirm = useCallback(() => {
      onUnsubscribeFromAllNotifications();
    }, [onUnsubscribeFromAllNotifications]);

    const closeUnsubscribeFromAllNotificationsModal = useCallback(() => {
      dispatch(CrmManagementActions.closeUnsubscribeFromAllNotificationsModalAction());
    }, []);

    return (
      <>
        <HidingTextTooltip
          tooltipContent="Subscribe to receive notificatioins for the directory items listed."
          width="150px"
          position="bottom"
          tooltipContentClassname="py-[5px] px-[8px]"
        >
          <FluidButton
            type="secondary"
            textClassName="flex items-center gap-[10px]"
            onClick={openSubscribeToAllNotificationsModal}
            disabled={directoryEntriesCount === 0}
          >
            Subscribe to all
            <RingingBellIcon className="w-5 h-5" fill={directoryEntriesCount === 0 ? theme.colors['gray-60'] : theme.colors['brown-prime']} />
          </FluidButton>
        </HidingTextTooltip>
        <HidingTextTooltip
          tooltipContent="Unsubscribe from directory items listed."
          width="150px"
          position="bottom"
          tooltipContentClassname="py-[5px] px-[8px]"
        >
          <FluidButton
            type="secondary"
            textClassName="flex items-center gap-[10px]"
            onClick={openUnsubscribeFromAllNotificationsModal}
            disabled={directoryEntriesCount === 0}
          >
            Unsubscribe all
            <StrikeBellIcon className="w-5 h-5" fill={directoryEntriesCount === 0 ? theme.colors['gray-60'] : theme.colors['brown-prime']} />
          </FluidButton>
        </HidingTextTooltip>

        <ConfirmationModal
          className="subscribe-to-all-notifications-confirmation"
          type={EConfirmationModalType.WARNING}
          isOpen={isSubscribeModalOpen}
          title={`You are about to subscribe to ${directoryEntriesCount} items that match your selected filter criteria.`}
          message="This operation may take several minutes..."
          confirmButtonLabel="Continue"
          cancelButtonLabel="Cancel"
          onConfirm={handleSubscribeToAllNotificationsConfirm}
          onCancel={closeSubscribeToAllNotificationsModal}
          isConfirmLoading={isNotificationsSubscriptionPending}
        />

        <ConfirmationModal
          className="unsubscribe-from-all-notifications-confirmation"
          type={EConfirmationModalType.WARNING}
          isOpen={isUnsubscribeModalOpen}
          title={`You are about to remove your subscription to the ${directoryEntriesCount} items that match your selected filter criteria.`}
          message="This operation may take several minutes. We'll notify you when the process is complete."
          confirmButtonLabel="Continue"
          cancelButtonLabel="Cancel"
          onConfirm={handleUnsubscribeFromAllNotificationsConfirm}
          onCancel={closeUnsubscribeFromAllNotificationsModal}
          isConfirmLoading={isNotificationsSubscriptionPending}
        />
      </>
    );
  }
);

